import React, { useCallback, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { filterBy } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { ReactDatePicker } from 'components/DatePicker/DatePicker'
import { Loading } from 'components/Loading'
import { format } from 'date-fns'
import { CompositeFilterDescriptor } from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'
import styled from 'styled-components'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { ExchangeRatesApiClient } from 'utils/api/exchangeRatesApi'
import { useFetchOne } from 'utils/api/useFetch'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'

const Container = styled.div`
  align-items: center;
  display: flex;

  > label {
    font-weight: bold;
    margin-right: 3rem;
  }

  > span {
    margin-left: 1rem;
    margin-right: 1rem;
  }

  select,
  input {
    margin: 0.5rem;
    color: #0e76eb;
    max-width: 200px;
    height: 32px;
    padding: 4px 11px;
    //color: rgba(0,0,0,.65);
    font-size: 14px;
    line-height: 1.5;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    -webkit-transition: all 0.3s;
    transition: all 0.3s;
    text-transform: capitalize;
  }
`

const RecordsExchangeRates = (): JSX.Element => {
  const [date, setDate] = useState<number | undefined>()
  const exchangeRatesApiClient = useApiClient(ExchangeRatesApiClient)
  const fetchPendingOrders = useCallback(
    (query?: PageQuery) => {
      if (date) {
        const dateFormat = format(date, 'yyyyMMdd')
        return exchangeRatesApiClient.getExchangeRatesQuery(query, dateFormat)
      }
      return exchangeRatesApiClient.getExchangeRatesQuery()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [date]
  )
  const { data = [], isLoading } = useFetchOne(fetchPendingOrders)
  const [dataState, setDataState] = useState<{
    skip: number
    take: number
    filter: CompositeFilterDescriptor
  }>({
    take: 20,
    skip: 0,
    filter: { filters: [], logic: 'and' },
  })

  const handleGridPageChange = (event: GridPageChangeEvent) => {
    setDataState({ ...dataState, skip: event.page.skip, take: event.page.take })
  }

  const onFilterChange = (e: GridFilterChangeEvent) => {
    setDataState({ ...dataState, filter: e.filter })
  }

  return (
    <React.Fragment>
      <Breadcrumbs crumbs={[{ title: 'Exchange Rates', link: links.recordsExchangeRates.url }]} />
      <Container>
        <label>Exchange Rate Date</label>
        <ReactDatePicker
          showYearDropdown
          dropdownMode='select'
          isClearable
          dateFormat='dd/MM/yyyy'
          selected={date ? new Date(date) : null}
          onChange={(e) => {
            if (e) {
              setDate(e.getTime())
            } else {
              setDate(undefined)
            }
          }}
          customInput={<input style={{ width: '100%' }} type='text' autoComplete='off' />}
        />
      </Container>

      <Loading isLoading={isLoading}>
        <Grid
          filterable
          resizable
          onPageChange={handleGridPageChange}
          data={filterBy(data, dataState.filter).slice(
            dataState.skip,
            dataState.take + dataState.skip
          )}
          take={dataState.take}
          skip={dataState.skip}
          filter={dataState.filter}
          onFilterChange={onFilterChange}
          total={data.length}
          pageable={pageableSettings}
        >
          <Column
            title='Base Currency'
            filter='text'
            filterable
            field='baseCurrency'
            className='table-columns__number'
          />
          <Column
            title='Quote Currency'
            filter='text'
            filterable
            field='quoteCurrency'
            className='table-columns__number'
          />
          <Column
            title='Date'
            cell={(props: GridCellProps) => (
              <td className='table-columns__date'>
                {formatLocalDateUTC(props.dataItem.dateTime, true)}
              </td>
            )}
            field='dateTime'
            filterable={false}
            className='table-columns__date'
          />
          <Column title='Rate' field='rate' filter='text' className='table-columns__number' />
        </Grid>
      </Loading>
    </React.Fragment>
  )
}

export default RecordsExchangeRates
